import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Head } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import PolaroidBoard from '../../components/LiveWall/PolaroidBoard.jsx';
import CinematicSlide from '../../components/LiveWall/CinematicSlide.jsx';
import SettingsPanel from '../../components/LiveWall/SettingsPanel.jsx';
import OnboardingOverlay from '../../components/LiveWall/OnboardingOverlay.jsx';
import WallQrPanel from '../../components/LiveWall/WallQrPanel.jsx';
import useLiveWallItems, { DEFAULT_SETTINGS } from '../../components/LiveWall/useLiveWallItems.js';
import { useGoogleCast } from '../../hooks.js';

const MODES = { CINEMATIC: 'cinematic', MOSAIC: 'mosaic' };
const CONTROLS_HIDE_MS = 3000;

function loadSettings(serverSettings) {
    // Server-side settings (shared with the cast/TV page) win over the
    // localStorage copy, which remains as a fallback for older events.
    if (serverSettings) {
        return { ...DEFAULT_SETTINGS, ...serverSettings };
    }
    try {
        const saved = localStorage.getItem('lw-settings');
        return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
        return DEFAULT_SETTINGS;
    }
}

export default function LiveIndex({ event, initialMedia, reloadUrl, wsChannel, wsAuthUrl, castAppId, settings: serverSettings, settingsUrl }) {
    const { t } = useTranslation();
    const {
        available: castAvailable,
        connected: castConnected,
        deviceName: castDeviceName,
        requestSession: requestCastSession,
        endSession: endCastSession,
    } = useGoogleCast(castAppId);
    const [settings, setSettings] = useState(() => loadSettings(serverSettings));

    // Another screen (or a second presenter tab) saved new settings
    const applyRemoteSettings = useCallback((remote) => {
        setSettings({ ...DEFAULT_SETTINGS, ...remote });
    }, []);

    const { items, newItemCount, simulatePhoto } = useLiveWallItems({
        initialMedia,
        reloadUrl,
        wsChannel,
        wsAuthUrl,
        onSettingsUpdated: applyRemoteSettings,
    });
    const [mode, setMode] = useState(() => {
        try {
            const saved = localStorage.getItem('lw-mode');
            return saved === MODES.CINEMATIC ? MODES.CINEMATIC : MODES.MOSAIC;
        } catch {
            return MODES.MOSAIC;
        }
    });
    const [showOnboarding, setShowOnboarding] = useState(() => {
        try { return !localStorage.getItem('lw-onboarding-seen'); }
        catch { return false; }
    });
    const [showControls, setShowControls] = useState(true);
    const [showSettings, setShowSettings] = useState(false);

    const dismissOnboarding = useCallback(() => {
        try { localStorage.setItem('lw-onboarding-seen', '1'); } catch {}
        setShowOnboarding(false);
    }, []);

    const settingsRef = useRef(settings);
    useEffect(() => { settingsRef.current = settings; }, [settings]);

    const updateSetting = useCallback((key, value) => {
        const next = { ...settingsRef.current, [key]: value };
        setSettings(next);
        try { localStorage.setItem('lw-settings', JSON.stringify(next)); } catch {}
        // Persist server-side so cast/TV screens pick the change up live
        if (settingsUrl) {
            axios.post(settingsUrl, next).catch(() => {});
        }
    }, [settingsUrl]);

    const hideTimer = useRef(null);

    // Controls auto-hide
    const showControlsTemporarily = useCallback(() => {
        setShowControls(true);
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setShowControls(false), CONTROLS_HIDE_MS);
    }, []);

    useEffect(() => {
        const onMove = () => showControlsTemporarily();
        document.addEventListener('mousemove', onMove);
        document.addEventListener('touchstart', onMove);
        showControlsTemporarily();
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('touchstart', onMove);
            clearTimeout(hideTimer.current);
        };
    }, [showControlsTemporarily]);

    const showQr = settings.showQr !== false && event.join_url;
    const [showCastModal, setShowCastModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const copyLink = useCallback(() => {
        navigator.clipboard.writeText(window.location.href).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, []);

    const handleCastClick = useCallback(() => {
        if (!castAvailable) {
            setShowCastModal(true);
            return;
        }

        if (castConnected) {
            endCastSession();
        } else {
            requestCastSession();
        }
    }, [castAvailable, castConnected, requestCastSession, endCastSession]);

    return (
        <>
            <Head title={`${event.name} · ${t('Live Photo Wall')}`} />

            <div className="lwf-fill fixed inset-0 bg-black overflow-hidden select-none">

                {/* Main display */}
                <div className="absolute inset-0">
                    {mode === MODES.CINEMATIC ? (
                        <CinematicSlide
                            items={items}
                            newItemCount={newItemCount}
                            slideDurationSec={settings.slideDuration}
                            settings={settings}
                        />
                    ) : (
                        <PolaroidBoard items={items} settings={settings} showQr={showQr} />
                    )}
                </div>

                {/* Controls overlay — auto-hides */}
                <div className={`absolute inset-0 z-30 pointer-events-none transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0'}`}>

                    {/* Top bar */}
                    <div className="absolute top-0 inset-x-0 flex items-center justify-between px-5 pt-4 pointer-events-auto">
                        <span className="text-white font-bold text-lg drop-shadow-lg">
                            {event.name}
                        </span>
                        <div className="flex items-center gap-2">
                            {/* Simulate button — dev only */}
                            {import.meta.env.DEV && (
                                <button
                                    onClick={simulatePhoto}
                                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full transition"
                                    title="Simulate a new photo arriving"
                                >
                                    📸 Test
                                </button>
                            )}
                            {/* Mode toggle */}
                            <button
                                onClick={() => setMode(m => {
                                    const next = m === MODES.CINEMATIC ? MODES.MOSAIC : MODES.CINEMATIC;
                                    try { localStorage.setItem('lw-mode', next); } catch {}
                                    return next;
                                })}
                                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full transition"
                            >
                                {mode === MODES.CINEMATIC ? `📌 ${t('Board')}` : `⛶ ${t('Cinematic')}`}
                            </button>
                            {/* Settings */}
                            <button
                                onClick={() => setShowSettings(s => !s)}
                                className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-sm w-8 h-8 rounded-full transition flex items-center justify-center"
                                title={t('Customize')}
                            >
                                ⚙
                            </button>
                        </div>
                    </div>

                    {/* Bottom-right: Cast to TV */}
                    <div className="absolute bottom-5 right-5 pointer-events-auto">
                        <button
                            onClick={handleCastClick}
                            className={`backdrop-blur-sm rounded-full px-3 py-2 flex items-center gap-2 text-xs transition ${
                                castConnected
                                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                                    : 'bg-white/10 hover:bg-white/20 text-white/70 hover:text-white'
                            }`}
                        >
                            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M1 18v3h3a3 3 0 0 0-3-3zm0-4v2a5 5 0 0 1 5 5H8a7 7 0 0 0-7-7zm0-4v2a9 9 0 0 1 9 9h2A11 11 0 0 0 1 10zm20-6H3C1.9 4 1 4.9 1 6v2h2V6h18v12h-7v2h7c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
                            </svg>
                            <span className="hidden md:inline">
                                {castConnected ? (castDeviceName ?? t('Casting…')) : t('Cast to TV')}
                            </span>
                        </button>
                    </div>

                </div>

                {/* QR code — always visible */}
                {showQr && <WallQrPanel joinUrl={event.join_url} />}
            </div>

            {showSettings && (
                <SettingsPanel
                    settings={settings}
                    onUpdate={updateSetting}
                    onClose={() => setShowSettings(false)}
                    mode={mode}
                />
            )}

            {showCastModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setShowCastModal(false)}>
                    <div
                        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-purple-600 px-6 py-5 text-white relative">
                            <button
                                onClick={() => setShowCastModal(false)}
                                className="absolute top-4 right-4 text-white/50 hover:text-white transition"
                            >✕</button>
                            <div className="flex items-center gap-2 mb-1">
                                <svg className="w-4 h-4 text-white/70" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M1 18v3h3a3 3 0 0 0-3-3zm0-4v2a5 5 0 0 1 5 5H8a7 7 0 0 0-7-7zm0-4v2a9 9 0 0 1 9 9h2A11 11 0 0 0 1 10zm20-6H3C1.9 4 1 4.9 1 6v2h2V6h18v12h-7v2h7c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
                                </svg>
                                <span className="text-xs font-semibold uppercase tracking-widest text-white/70">{t('Live Photo Wall')}</span>
                            </div>
                            <h2 className="text-xl font-bold">{t('Cast to your TV')}</h2>
                            <p className="text-sm text-white/80 mt-1">
                                {isIos
                                    ? t("Casting isn't available from an iPhone browser.")
                                    : t('Show the wall on any screen in seconds.')}
                            </p>
                        </div>

                        {/* Steps */}
                        <ol className="px-6 py-5 space-y-4">
                            {(isIos ? [
                                t('Copy the link below.'),
                                t('Open it on a laptop or Android phone connected to your TV or Chromecast.'),
                                t('Use the cast button there to show the wall on your TV.'),
                            ] : [
                                t('Open this page in Google Chrome on the computer connected to your TV or Chromecast.'),
                                t('Click the menu in the top-right corner and choose "Cast".'),
                                t('Select your TV or Chromecast from the list.'),
                                t("Every guest's photo will appear live on your screen."),
                            ]).map((text, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                                    <span className="text-gray-600 text-sm leading-snug">{text}</span>
                                </li>
                            ))}
                        </ol>

                        <div className="px-6 pb-5">
                            <button
                                onClick={copyLink}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm py-2.5 rounded-xl transition"
                            >
                                {copied ? `✓ ${t('Link copied!')}` : t('Copy page link')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showOnboarding && <OnboardingOverlay onDismiss={dismissOnboarding} />}
        </>
    );
}
